import "bootstrap/dist/css/bootstrap.min.css"
import { SnackbarProvider } from "notistack"
import { useEffect, useState } from "react"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { ErrorBoundary } from "react-error-boundary"
import { BrowserRouter } from "react-router-dom"

import { Button, makeStyles } from "@material-ui/core"

import Permissoes from "./permissoes"
import DialogMensagem from "./components/DialogMensagem"
import PopupEnquete from "./components/PopupEnquete"

import flatErro from "./assets/fail-flat.svg"
import { RegistrarLogErro } from "./services/clientefiel"
import { clearWhatsappOfficialSession } from "./utils/whatsappOfficialSession"

import TagManager from "react-gtm-module"
import { ParametrosContextProvider } from "./contexts/ParametrosContext"
import GoogleMapsProvider from "./pages/GoogleMaps/GoogleMapsProvider"
import useAppStore from "./stores/AppStore"

const tagManagerArgs = { gtmId: "GTM-P7ZLCTD" }

const useStyles = makeStyles(theme => ({
    // As faixas nascem coladas no topo da viewport, onde ficam o cabeçalho fixo e
    // as faixas de alerta. --banner-height é publicada pelo Cabecalho conforme
    // essas faixas aparecem/somem (ver global.css).
    containerFaixa: {
        top: "calc(56px + var(--banner-height, 0px) + 8px)",
        [theme.breakpoints.up("sm")]: {
            top: "calc(64px + var(--banner-height, 0px) + 8px)",
        },
    },
}))

function ErrorHandler({ error }) {
    useEffect(() => {
        RegistrarLogErro(error, "Render - Recebedor de Pedidos")
    }, [])

    const [today] = useState(new Date())
    const [date] = useState(
        today.getFullYear() + "-" + (today.getMonth() + 1) + "-" + today.getDate()
    )
    const [time] = useState(today.getHours() + ":" + today.getMinutes())

    function handleDeslogar() {
        clearWhatsappOfficialSession()
        localStorage.removeItem("chaveSistema")
        localStorage.removeItem("dataUltimoServico")
        localStorage.removeItem("estabelecimentoAtualCF")
        window.name = ""
        window.location.href = "/login"
    }

    return (
        <div
            style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                padding: "2em",
                position: "absolute",
            }}
        >
            <img
                src={flatErro}
                alt={"loading erro"}
                className="pretoEBranco"
                style={{ width: "350px", marginBottom: "1em" }}
            />
            <h6>Houve um erro no aplicativo!</h6>
            <p>
                Tire um print dessa tela e envie para os desenvolvedores. Em seguida, clique em
                "VOLTAR" para tentar novamente ou em "DESLOGAR" para sair do sistema.
            </p>
            <p>Erro: {error.message}</p>
            <span style={{ marginBottom: "24px" }}>
                {date} - {time}
            </span>
            <div style={{ display: "flex", gap: "1em" }}>
                <Button
                    variant="outlined"
                    color="default"
                    onClick={() => {
                        window.location.href = "/"
                    }}
                >
                    VOLTAR
                </Button>
                <Button variant="outlined" color="default" onClick={handleDeslogar}>
                    DESLOGAR
                </Button>
            </div>
            <span style={{ marginTop: "1em", color: "rgba(0,0,0,0.6)" }}>
                Ao clicar em <span style={{ color: "var(--cor-erro)" }}>'Deslogar'</span>, você sairá do sistema,
                mas suas configurações locais (como a impressora) serão mantidas.
            </span>
        </div>
    )
}

function App() {
    const classes = useStyles()
    const configuracoes = useAppStore(s => s.configuracoes)
    const apiKey = configuracoes?.user?.parametros?.tokenGoogleMaps

    useEffect(() => {
        TagManager.initialize(tagManagerArgs)
    }, [])

    return (
        <ErrorBoundary FallbackComponent={ErrorHandler}>
            <ParametrosContextProvider>
                <SnackbarProvider
                    maxSnack={3}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    classes={{ containerAnchorOriginTopRight: classes.containerFaixa }}
                >
                    <DndProvider backend={HTML5Backend}>
                        {/* Popup central de erro/aviso. Fica aqui, fora do BrowserRouter,
                            para sobreviver à troca de tela: alimentado pela fila do
                            DialogMensagemStore, que o useSnackbar de utils/notificacoes
                            preenche a partir de qualquer página. */}
                        <DialogMensagem />
                        <PopupEnquete />
                        {apiKey ? (
                            <GoogleMapsProvider apiKey={apiKey}>
                                <BrowserRouter>
                                    <Permissoes />
                                </BrowserRouter>
                            </GoogleMapsProvider>
                        ) : (
                            <BrowserRouter>
                                <Permissoes />
                            </BrowserRouter>
                        )}
                    </DndProvider>
                </SnackbarProvider>
            </ParametrosContextProvider>
        </ErrorBoundary>
    )
}

export default App
